import React, { FC, memo } from "react";
import { useSelector } from "react-redux";
import { ResourceTypeLink, ServiceListItemDc } from "@evergis/api";
import { FlatButton } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { useNewLayerFromData } from "hooks/useDialogs";
import { useLimitsExceededCheck } from "../../../../hooks/useLimitsExceededCheck";

import { isEditable } from "../../../../evergis/core/permissions";
import { getAnySelected, getCategory } from "../../../../ducks/catalog/selectors";
import { TableControlsContainer, TableControlSlot } from "./styled";
import { InfoIcon } from "../../../../components/ResourceActions/InfoIcon";
import { ApplyLabel } from "../Card/ApplyLabel";
import { useResourceType } from "../../../../hooks/useResourceType";
import { ResourceCategory } from "../../hooks/useFetchParams";
import { useOpenProject } from "../../../../hooks/project/useOpenProject";
import { useProjectName } from "../../../../hooks/project/useProjectName";

import { CardControlsProps } from "../../types";

// eslint-disable-next-line max-lines-per-function
const TableControlsComponent: FC<CardControlsProps> = ({ resource, config }) => {
  const { t } = useTranslation();
  const { permissions, name } = resource as ServiceListItemDc;
  const [resourceType] = useResourceType();
  const someSelected = useSelector(getAnySelected);
  const category = useSelector(getCategory(resourceType));
  const editable = isEditable(permissions);
  const processedCreateLayer = useLimitsExceededCheck(useNewLayerFromData());
  const openProject = useOpenProject(name);
  const currentProjectName = useProjectName();
  const opened = currentProjectName === resource.name;

  const isSharedResource = category === ResourceCategory.Shared;

  return (
    <TableControlsContainer>
      <TableControlSlot>
        {(!editable || isSharedResource) && (
          <InfoIcon disabled={someSelected} title={config.infoIcon.title} name={name} />
        )}
        {editable && !isSharedResource && resourceType === ResourceTypeLink.Table && (
          <FlatButton onClick={() => processedCreateLayer([name, ResourceTypeLink.Layer])} primary>
            {t("newLayer", { ns: "catalog" })}
          </FlatButton>
        )}
        {resourceType === ResourceTypeLink.Project && (
          <FlatButton onClick={openProject} primary disabled={opened}>
            <ApplyLabel
              text={t("actions.open", { ns: "common" })}
              appliedText={t("projectCardOpened", { ns: "catalog" })}
              applied={opened}
            />
          </FlatButton>
        )}
      </TableControlSlot>
    </TableControlsContainer>
  );
};

export const TableControls: FC<CardControlsProps> = memo(TableControlsComponent);
